import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiStar, FiTrendingUp, FiHome, FiCalendar } = FiIcons;

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Marco Rossi",
      location: "Milano Centro",
      property: "Loft Design 85mq",
      propertyType: "Loft Moderno",
      rating: 5,
      text: "Con Elvira ho trasformato completamente il rendimento del mio loft. Da un affitto tradizionale di €1.200/mese, ora genero circa €2.400/mese con gli affitti brevi. La gestione è completamente automatizzata e non devo preoccuparmi di nulla!",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€1.200/mese",
      afterRevenue: "€2.400/mese",
      increase: "+100%",
      period: "8 mesi di collaborazione",
      highlights: ["Gestione H24", "Rating 4.9/5", "Zero stress"]
    },
    {
      id: 2,
      name: "Laura Bianchi",
      location: "Firenze Centro Storico",
      property: "Appartamento Rinascimentale",
      propertyType: "Dimora Storica",
      rating: 5,
      text: "Servizio impeccabile! Elvira ha valorizzato al massimo il mio appartamento storico. La comunicazione è costante, gli ospiti sono sempre entusiasti e le recensioni sono eccellenti. Ho aumentato i guadagni del 60% rispetto all'affitto normale.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b647?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€1.500/mese",
      afterRevenue: "€2.400/mese",
      increase: "+60%",
      period: "1 anno di collaborazione",
      highlights: ["Superhost status", "98% occupancy", "Zero reclami"]
    },
    {
      id: 3,
      name: "Giuseppe Verdi",
      location: "Costa Toscana",
      property: "Villa con Piscina",
      propertyType: "Villa di Lusso",
      rating: 5,
      text: "La professionalità di Elvira è straordinaria. Ha trasformato la mia villa in una macchina da soldi! Dalla fotografia professionale al pricing dinamico, ogni dettaglio è curato. Risultati oltre ogni aspettativa: +150% di ricavi!",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€2.000/mese",
      afterRevenue: "€5.000/mese",
      increase: "+150%",
      period: "6 mesi di collaborazione",
      highlights: ["Lusso premium", "Clientela internazionale", "ROI eccezionale"]
    },
    {
      id: 4,
      name: "Giulia Marchetti",
      location: "Roma Trastevere",
      property: "B&B Boutique",
      propertyType: "Boutique Accommodation",
      rating: 5,
      text: "Gestivo da sola il mio B&B e era diventato stressante. Elvira ha rivoluzionato tutto: ora ho più tempo libero e guadagno il doppio! La sua esperienza nel revenue management è incredibile. Consiglio assolutamente!",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€1.800/mese",
      afterRevenue: "€3.600/mese",
      increase: "+100%",
      period: "10 mesi di collaborazione",
      highlights: ["Tempo libero", "Stress zero", "Ricavi doppi"]
    },
    {
      id: 5,
      name: "Alessandro Torelli",
      location: "Cinque Terre",
      property: "Casa Vista Mare",
      propertyType: "Casa Vacanze",
      rating: 5,
      text: "Avevo una casa che usavo solo d'estate. Elvira l'ha trasformata in una fonte di reddito tutto l'anno! Il pricing stagionale intelligente e la gestione impeccabile hanno portato risultati incredibili. Investment perfetto!",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€800/mese",
      afterRevenue: "€2.200/mese",
      increase: "+175%",
      period: "1 anno di collaborazione",
      highlights: ["Reddito annuale", "Pricing dinamico", "Vista mare premium"]
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000); // Increased to 8 seconds for more content

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative max-w-6xl mx-auto">
      <div className="bg-gradient-to-br from-white to-warm-50 rounded-2xl shadow-2xl overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="p-8 md:p-12"
          >
            {/* Header with stars */}
            <div className="flex justify-center mb-8">
              {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                <SafeIcon 
                  key={i} 
                  icon={FiStar} 
                  className="h-8 w-8 text-yellow-400 fill-current" 
                />
              ))}
            </div>

            {/* Main testimonial */}
            <blockquote className="text-xl md:text-2xl text-warm-700 mb-8 leading-relaxed text-center">
              "{testimonials[currentIndex].text}"
            </blockquote>

            {/* Results section */}
            <div className="bg-primary-50 rounded-xl p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-red-600 mb-1">
                    {testimonials[currentIndex].beforeRevenue}
                  </div>
                  <div className="text-sm text-warm-600">Prima</div>
                </div>
                <div className="flex items-center justify-center">
                  <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-primary-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 mb-1">
                    {testimonials[currentIndex].afterRevenue}
                  </div>
                  <div className="text-sm text-warm-600">Dopo</div>
                </div>
              </div>
              <div className="text-center mt-4">
                <span className="bg-primary-600 text-white px-4 py-2 rounded-full font-bold">
                  {testimonials[currentIndex].increase} di aumento
                </span>
              </div>
            </div>

            {/* Client info */}
            <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8 mb-6">
              <div className="flex items-center space-x-4">
                <img
                  src={testimonials[currentIndex].avatar}
                  alt={testimonials[currentIndex].name}
                  className="w-20 h-20 rounded-full border-4 border-primary-200 shadow-lg"
                />
                <div className="text-left">
                  <div className="font-bold text-warm-900 text-xl">
                    {testimonials[currentIndex].name}
                  </div>
                  <div className="text-warm-600 font-medium">
                    {testimonials[currentIndex].property}
                  </div>
                  <div className="text-sm text-warm-500 flex items-center">
                    <SafeIcon icon={FiHome} className="h-4 w-4 mr-1" />
                    {testimonials[currentIndex].location}
                  </div>
                </div>
              </div>

              <div className="text-center md:text-left">
                <div className="text-sm text-warm-500 mb-2 flex items-center justify-center md:justify-start">
                  <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1" />
                  {testimonials[currentIndex].period}
                </div>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {testimonials[currentIndex].highlights.map((highlight, i) => (
                    <span key={i} className="bg-warm-200 text-warm-800 px-2 py-1 rounded-md text-xs">
                      ✓ {highlight}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Property type badge */}
            <div className="text-center">
              <span className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-full text-sm font-medium">
                {testimonials[currentIndex].propertyType}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center px-8 pb-8">
          <button
            onClick={prevTestimonial}
            className="bg-white hover:bg-primary-50 p-4 rounded-full transition-colors duration-200 shadow-lg border border-warm-200"
            aria-label="Testimonianza precedente"
          >
            <SafeIcon icon={FiChevronLeft} className="h-6 w-6 text-warm-600" />
          </button>

          {/* Dots indicator */}
          <div className="flex space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-4 h-4 rounded-full transition-all duration-200 ${
                  index === currentIndex 
                    ? 'bg-primary-600 scale-125' 
                    : 'bg-warm-300 hover:bg-primary-300'
                }`}
                aria-label={`Vai alla testimonianza ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={nextTestimonial}
            className="bg-white hover:bg-primary-50 p-4 rounded-full transition-colors duration-200 shadow-lg border border-warm-200"
            aria-label="Testimonianza successiva"
          >
            <SafeIcon icon={FiChevronRight} className="h-6 w-6 text-warm-600" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-8 pb-6">
          <div className="w-full bg-warm-200 rounded-full h-2">
            <motion.div
              className="bg-primary-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIndex + 1) / testimonials.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <div className="flex justify-between text-xs text-warm-500 mt-2">
            <span>Testimonianza {currentIndex + 1} di {testimonials.length}</span>
            <span>Clienti reali • Risultati verificati</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCarousel;